import { Injectable, inject } from '@angular/core';
import { Observable, throwError, of } from 'rxjs';
import { map, switchMap, catchError } from 'rxjs/operators';
import { FormFieldMapper, FormField } from '@shared';
import { GetInvestigationFormVersionByIdUseCase } from '@features/investigation-forms';
import { GetInvestigationFieldValuesUseCase } from '../get-investigation-field-values/get-investigation-field-values.use-case';
import { InvestigationsState } from '../../states/investigations.state';
import { GetInvestigationFormFieldsCommand } from './get-investigation-form-fields.command';

@Injectable({ providedIn: 'root' })
export class GetInvestigationFormFieldsUseCase {
  private readonly _getFormVersionUseCase = inject(GetInvestigationFormVersionByIdUseCase);
  private readonly _getFieldValuesUseCase = inject(GetInvestigationFieldValuesUseCase);
  private readonly _formFieldMapper = inject(FormFieldMapper);
  private readonly _state = inject(InvestigationsState);

  public execute(command: GetInvestigationFormFieldsCommand): Observable<FormField[]> {
    const { investigationFormVersionId, investigationId, forceRefresh = false } = command;

    return this._getFormVersionUseCase.execute({ versionId: investigationFormVersionId }).pipe(
      switchMap((formDetail) => {
        if (!formDetail?.fields) {
          throw new Error('Invalid investigation form version: missing fields');
        }

        const formFields = this._formFieldMapper.mapToFormFields(formDetail.fields);

        const investigation = this._state.selectedInvestigation();
        const hasFieldValues = investigation?.fieldValues && investigation.fieldValues.length > 0;

        if (hasFieldValues && !forceRefresh) {
          const fieldValuesMap = new Map(
            investigation.fieldValues.map(fv => [fv.versionFieldId, fv.value])
          );
          return of(formFields.map(field => ({
            ...field,
            defaultValue: this._parseFieldValue(fieldValuesMap.get(field.id) ?? null, field.type),
          })) as FormField[]);
        }

        return this._getFieldValuesUseCase.execute({ investigationId }).pipe(
          map((result) => {
            const fieldValuesMap = new Map(
              result.fieldValues.map(fv => [fv.versionFieldId, fv.value])
            );
            return formFields.map(field => ({
              ...field,
              defaultValue: this._parseFieldValue(fieldValuesMap.get(field.id) ?? null, field.type),
            })) as FormField[];
          })
        );
      }),
      catchError((error) => throwError(() => new Error(error.message || 'Failed to load form fields')))
    );
  }

  private _parseFieldValue(value: string | null | undefined, fieldType: string): string | number | boolean | Date | string[] | null {
    if (!value) return null;
    try {
      const parsed = JSON.parse(value);
      if (fieldType === 'date' && typeof parsed === 'string') {
        const d = new Date(parsed);
        return isNaN(d.getTime()) ? null : d;
      }
      if (fieldType === 'checkbox' && Array.isArray(parsed)) return parsed.map(String);
      if (typeof parsed === 'string' || typeof parsed === 'number' || typeof parsed === 'boolean') return parsed;
      return JSON.stringify(parsed);
    } catch { return value; }
  }
}
